import { useState, useEffect, useCallback } from "react";
import { t, font, Button, Input, Badge } from "@realtyflows/ui";
import {
  getFolderCommission,
  upsertFolderCommission,
  addCommissionLineItem,
  updateCommissionLineItem,
  deleteCommissionLineItem,
  generateCda,
} from "../api";

/**
 * FolderCommission — V3 Features 3.12, 3.13, 3.14
 *
 * Per-folder commission ledger with disbursement tracking + CDA generation.
 *
 * Layout:
 *   - Header: sale price, total commission, rate
 *   - Line items: payee, type, basis, amount, status (pending → confirmed)
 *   - Actions: generate CDA, regenerate
 */

const PAYEE_TYPE_OPTIONS = [
  { id: "listing_agent", label: "Listing Agent" },
  { id: "listing_broker", label: "Listing Broker" },
  { id: "buying_agent", label: "Buying Agent" },
  { id: "buying_broker", label: "Buying Broker" },
  { id: "referral", label: "Referral" },
  { id: "franchise", label: "Franchise" },
  { id: "other", label: "Other" },
];

const STATUS_META = {
  pending:    { label: "Pending",    color: "muted" },
  authorized: { label: "Authorized", color: "amber" },
  issued:     { label: "Issued",     color: "sky" },
  confirmed:  { label: "Confirmed",  color: "emerald" },
};
const colorOf = (n) => ({ emerald: t.emerald, amber: t.amber, sky: t.sky, coral: t.coral, muted: t.textMuted })[n] || t.textMuted;

const fmtMoney = (cents) => {
  if (cents == null) return "—";
  return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};
const parseMoney = (str) => {
  const num = parseFloat(String(str).replace(/[^\d.-]/g, ""));
  return isNaN(num) ? 0 : Math.round(num * 100);
};

export default function FolderCommission({ folderId }) {
  const [data, setData] = useState({ commission: null, lineItems: [] });
  const [loading, setLoading] = useState(true);
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerDraft, setHeaderDraft] = useState({ sale_price: "", total_commission: "", rate: "" });
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({ payee_type: "listing_agent", payee_name: "", payee_email: "", amount: "", split_basis: "", split_value: "" });
  const [busy, setBusy] = useState(false);
  const [cdaBusy, setCdaBusy] = useState(false);

  const refresh = useCallback(() => {
    if (!folderId) return;
    setLoading(true);
    getFolderCommission(folderId)
      .then((d) => {
        setData(d);
        if (d.commission) {
          setHeaderDraft({
            sale_price: d.commission.sale_price_cents ? (d.commission.sale_price_cents / 100).toString() : "",
            total_commission: d.commission.total_commission_cents ? (d.commission.total_commission_cents / 100).toString() : "",
            rate: d.commission.commission_rate_bps ? (d.commission.commission_rate_bps / 100).toString() : "",
          });
        }
      })
      .catch(() => setData({ commission: null, lineItems: [] }))
      .finally(() => setLoading(false));
  }, [folderId]);
  useEffect(refresh, [refresh]);

  const handleSaveHeader = async () => {
    setBusy(true);
    try {
      await upsertFolderCommission(folderId, {
        sale_price_cents: parseMoney(headerDraft.sale_price),
        total_commission_cents: parseMoney(headerDraft.total_commission),
        commission_rate_bps: headerDraft.rate ? Math.round(parseFloat(headerDraft.rate) * 100) : null,
      });
      setEditingHeader(false);
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.payee_name?.trim()) {
      window.alert("Payee name is required");
      return;
    }
    setBusy(true);
    try {
      await addCommissionLineItem(folderId, {
        payee_type: newItem.payee_type,
        payee_name: newItem.payee_name.trim(),
        payee_email: newItem.payee_email || null,
        amount_cents: parseMoney(newItem.amount),
        split_basis: newItem.split_basis || null,
        split_value: newItem.split_value ? parseFloat(newItem.split_value) : null,
      });
      setNewItem({ payee_type: "listing_agent", payee_name: "", payee_email: "", amount: "", split_basis: "", split_value: "" });
      setShowAddItem(false);
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const handleStatusChange = async (item, status) => {
    setBusy(true);
    try {
      await updateCommissionLineItem(item.id, { status });
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not update");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveItem = async (item) => {
    if (!window.confirm(`Remove ${item.payee_name}?`)) return;
    try { await deleteCommissionLineItem(item.id); refresh(); }
    catch (err) { window.alert(err.message || "Could not remove"); }
  };

  const handleGenerateCda = async () => {
    setCdaBusy(true);
    try {
      await generateCda(folderId);
      window.alert("CDA PDF generated and added to folder uploads");
      refresh();
    } catch (err) {
      window.alert(err.message || "Could not generate CDA");
    } finally {
      setCdaBusy(false);
    }
  };

  if (loading) {
    return <div style={{ fontSize: 12, color: t.textMuted, padding: 10, fontFamily: font.body }}>Loading commission...</div>;
  }

  const c = data.commission;
  const items = data.lineItems;
  const totalDisbursed = items.reduce((sum, i) => sum + (parseInt(i.amount_cents) || 0), 0);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr) auto", gap: 12, marginBottom: 14 }}>
        <HeaderField
          label="Sale Price"
          value={fmtMoney(c?.sale_price_cents)}
          editing={editingHeader}
          editValue={headerDraft.sale_price}
          onEditChange={(v) => setHeaderDraft({ ...headerDraft, sale_price: v })}
        />
        <HeaderField
          label="Total Commission"
          value={fmtMoney(c?.total_commission_cents)}
          editing={editingHeader}
          editValue={headerDraft.total_commission}
          onEditChange={(v) => setHeaderDraft({ ...headerDraft, total_commission: v })}
        />
        <HeaderField
          label="Rate"
          value={c?.commission_rate_bps != null ? `${(c.commission_rate_bps / 100).toFixed(2)}%` : "—"}
          editing={editingHeader}
          editValue={headerDraft.rate}
          onEditChange={(v) => setHeaderDraft({ ...headerDraft, rate: v })}
          suffix="%"
        />
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6 }}>
          {editingHeader ? (
            <>
              <Button variant="primary" size="sm" onClick={handleSaveHeader} disabled={busy}>Save</Button>
              <Button variant="outline" size="sm" onClick={() => setEditingHeader(false)}>Cancel</Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setEditingHeader(true)}>Edit</Button>
          )}
        </div>
      </div>

      {/* Line items header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
          Disbursement Schedule
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <Button variant="outline" size="sm" onClick={() => setShowAddItem((v) => !v)}>
            {showAddItem ? "Cancel" : "+ Add payee"}
          </Button>
          <Button variant="primary" size="sm" onClick={handleGenerateCda} disabled={cdaBusy || items.length === 0}>
            {cdaBusy ? "Generating..." : c?.cda_generated_at ? "Regenerate CDA" : "Generate CDA"}
          </Button>
        </div>
      </div>

      {/* New item form */}
      {showAddItem && (
        <div style={{ padding: 12, background: t.surfaceWarm, border: `1px solid ${t.border}`, borderRadius: 8, marginBottom: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
            <select
              value={newItem.payee_type}
              onChange={(e) => setNewItem({ ...newItem, payee_type: e.target.value })}
              style={{ padding: "8px 10px", fontSize: 12, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surface, color: t.textPrimary, fontFamily: font.body, outline: "none" }}
            >
              {PAYEE_TYPE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <Input placeholder="Payee name" value={newItem.payee_name} onChange={(e) => setNewItem({ ...newItem, payee_name: e.target.value })} />
            <Input placeholder="Payee email (optional)" value={newItem.payee_email} onChange={(e) => setNewItem({ ...newItem, payee_email: e.target.value })} />
            <Input placeholder="Amount ($)" value={newItem.amount} onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })} />
            <select
              value={newItem.split_basis}
              onChange={(e) => setNewItem({ ...newItem, split_basis: e.target.value })}
              style={{ padding: "8px 10px", fontSize: 12, border: `1.5px solid ${t.border}`, borderRadius: 6, background: t.surface, color: t.textPrimary, fontFamily: font.body, outline: "none" }}
            >
              <option value="">Basis (optional)</option>
              <option value="pct_of_total">% of total</option>
              <option value="pct_of_side">% of side</option>
              <option value="fixed">Fixed</option>
            </select>
            <Input placeholder="Split value (e.g. 50 for 50%)" value={newItem.split_value} onChange={(e) => setNewItem({ ...newItem, split_value: e.target.value })} />
          </div>
          <div style={{ marginTop: 8 }}>
            <Button variant="primary" size="sm" onClick={handleAddItem} disabled={busy}>
              {busy ? "Adding..." : "Add line item"}
            </Button>
          </div>
        </div>
      )}

      {/* Line items table */}
      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: t.textMuted, fontStyle: "italic", padding: 14, textAlign: "center", fontFamily: font.body }}>
          No line items yet. Add a payee to start building the disbursement schedule.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: font.body }}>
            <thead>
              <tr style={{ background: t.surfaceWarm, color: t.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                {["Payee", "Type", "Basis", "Amount", "Status", ""].map((h) => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", borderBottom: `1px solid ${t.border}`, fontFamily: font.mono, fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const meta = STATUS_META[item.status] || STATUS_META.pending;
                return (
                  <tr key={item.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td style={{ padding: 10, color: t.textPrimary }}>
                      <div style={{ fontWeight: 600 }}>{item.payee_name}</div>
                      {item.payee_email && <div style={{ fontSize: 10, color: t.textMuted }}>{item.payee_email}</div>}
                    </td>
                    <td style={{ padding: 10, fontSize: 11 }}>{item.payee_type.replace(/_/g, " ")}</td>
                    <td style={{ padding: 10, fontSize: 11 }}>
                      {item.split_basis === "pct_of_total" ? `${item.split_value}% of total`
                       : item.split_basis === "pct_of_side" ? `${item.split_value}% of side`
                       : item.split_basis === "fixed" ? "Fixed"
                       : "—"}
                    </td>
                    <td style={{ padding: 10, fontFamily: font.mono, fontWeight: 700 }}>{fmtMoney(item.amount_cents)}</td>
                    <td style={{ padding: 10 }}>
                      <select
                        value={item.status}
                        onChange={(e) => handleStatusChange(item, e.target.value)}
                        disabled={busy}
                        style={{
                          fontSize: 11, padding: "4px 8px", borderRadius: 6,
                          border: `1px solid ${t.border}`, background: t.surfaceWarm,
                          color: colorOf(meta.color), fontFamily: font.body, cursor: "pointer", fontWeight: 600,
                        }}
                      >
                        {Object.entries(STATUS_META).map(([k, m]) => (
                          <option key={k} value={k}>{m.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ padding: 10 }}>
                      <button onClick={() => handleRemoveItem(item)} style={{ background: "transparent", border: "none", color: t.coral, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>×</button>
                    </td>
                  </tr>
                );
              })}
              <tr style={{ background: t.surfaceWarm }}>
                <td colSpan={3} style={{ padding: 10, fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, textAlign: "right" }}>
                  TOTAL DISBURSED
                </td>
                <td style={{ padding: 10, fontFamily: font.mono, fontWeight: 800, color: t.textPrimary }}>
                  {fmtMoney(totalDisbursed)}
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {c?.cda_generated_at && (
        <div style={{ marginTop: 10, fontSize: 11, color: t.textMuted, fontFamily: font.body, textAlign: "right" }}>
          CDA generated {new Date(c.cda_generated_at).toLocaleString()} · See uploads section
        </div>
      )}
    </div>
  );
}

function HeaderField({ label, value, editing, editValue, onEditChange, suffix }) {
  return (
    <div style={{ background: t.surfaceWarm, borderRadius: 6, padding: "8px 12px" }}>
      <div style={{ fontSize: 9, color: t.textMuted, fontFamily: font.mono, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 3 }}>
        {label}
      </div>
      {editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <input
            type="number"
            value={editValue}
            onChange={(e) => onEditChange(e.target.value)}
            style={{
              width: "100%", padding: "4px 6px", fontSize: 14, fontWeight: 700, fontFamily: font.mono,
              border: `1px solid ${t.border}`, borderRadius: 4, background: t.surface, color: t.textPrimary, outline: "none",
            }}
          />
          {suffix && <span style={{ fontSize: 11, color: t.textMuted, fontFamily: font.mono }}>{suffix}</span>}
        </div>
      ) : (
        <div style={{ fontSize: 16, fontWeight: 800, color: t.textPrimary, fontFamily: font.mono }}>
          {value}
        </div>
      )}
    </div>
  );
}
